import React, {useState} from 'react';
import {Form, ListGroupItem} from 'react-bootstrap';
import {FacetComponent} from './facetComponent';
import {FacetGroup, FacetOnChange, HibuFacet, SelectionType} from './facetTypes';
import produce from 'immer';
import {ReduxNormalizedType} from '../app-types';
import {useTranslation} from 'react-i18next';
import {Icons} from '../icons/icons';
import {SvgIcon} from '../icons/svgIcon';
import {FacetHistogramComponent} from './facetHistogramComponent';
import {formatDateShort} from '../utils/utils';
import {HibuTooltip} from '../common/hibuTooltip/hibuTooltip';
import {useLocalizationUtil} from '../localizationUtil';
import {FacetGroupHeader} from './facetGroupHeader';

type FacetGroupComponentProps = {
    group: FacetGroup;
    onChange: FacetOnChange;
    withTooltip?: boolean;
    onExpandCollapse?: (fieldName: string) => void;
    isExpanded: boolean;
    normalizedValues: ReduxNormalizedType<HibuFacet>;
    selectionType: SelectionType;
    selectedFacetValues: string[];
    facetListRef: React.MutableRefObject<HTMLDivElement | null>;
};

const DEFAULT_FACET_VALUES_VISIBLE_COUNT = 8;

export const FacetGroupComponent = React.memo((props: FacetGroupComponentProps) => {
    const {t} = useTranslation();

    const isMultiSelect = props.selectionType === SelectionType.MULTI;
    const isHistogram = props.selectionType === SelectionType.RANGE;

    const [filter, setFilter] = useState('');
    const [showMore, setShowMore] = useState(false);
    const [tooltipValue, setTooltipValue] = useState('');
    const {formatNumber} = useLocalizationUtil();

    let allMatchingFacetValuesCount: number; // the number of facet values that match the current filter (if there is any) or are selected
    let unselectedFacetValuesThatAreVisibleWhenShowMoreIsFalseCount: number;

    const handleFacetToggled = (facet: HibuFacet): void => {
        let newSelectedValues: string[] = [];

        const facetSelected = isFacetSelected(facet);

        if (!isMultiSelect && facetSelected) {
            // selecting an already selected facet with single select doesn't change anything
            return;
        }

        if (isMultiSelect) {
            if (facetSelected) {
                newSelectedValues = props.selectedFacetValues.filter((value) => value !== facet.value);
            } else {
                // if the group never had any selected values before, `selectedFacetValues` is undefined
                newSelectedValues = produce(props.selectedFacetValues || [], (draftSelectedValues) => {
                    draftSelectedValues.push(facet.value);
                });
            }
        } else {
            // single selection only
            newSelectedValues.push(facet.value);
        }
        props.onChange(props.group, newSelectedValues);
    };

    const handleFacetMouseEnter = (facet: HibuFacet): void => {
        setTooltipValue(facet.value);
    };

    const handleFacetMouseLeave = (facet: HibuFacet): void => {
        if (facet.value === tooltipValue) {
            setTooltipValue('');
        }
    };

    const handleFacetRangeUpdated = (lowerBoundValue: number, upperBoundValue: number, range: number[]): void => {
        let newSelectedValues: string[];
        if (lowerBoundValue === range[0] && upperBoundValue === range[1]) {
            newSelectedValues = [];
        } else {
            newSelectedValues = [new Date(lowerBoundValue).toISOString(), new Date(upperBoundValue).toISOString()];
        }
        props.onChange(props.group, newSelectedValues);
    };

    const onClickHeader = () => {
        hasFacetValues() &&
        props.onExpandCollapse &&
        props.onExpandCollapse(props.group.fieldName) &&
        setShowMore(false);
    };

    const renderFacetContainer = () => {
        let values = props.normalizedValues;
        if (values.allKeys.length === 0) {
            return null;
        }

        if (isHistogram && props.isExpanded) {
            const expandedHistogram: JSX.Element[] = renderExpandedHistogram(values);
            return <div className={'facet-values'}>{expandedHistogram}</div>;
        }

        if (isHistogram && !props.isExpanded) {
            const collapsedHistogram: JSX.Element[] = renderCollapsedHistogram();
            return <div className={'facet-values'}>{collapsedHistogram}</div>;
        }

        // if we get here, we have a standard facet (i.e. no histogram)
        if (isCollapsed()) {
            values = toSelectedHibuFacetValues();
        }
        const facetComponents: JSX.Element[] = renderMultiselectFacets(values);
        const showMoreComponent: JSX.Element | null = renderShowMore();

        return (
            <Form
                key={'form-' + props.group.fieldName}
                onSubmit={(event) => event.preventDefault()}
                className="facet-values"
            >
                {facetComponents}
                {showMoreComponent}
            </Form>
        );
    };

    const renderMultiselectFacets = (facets: ReduxNormalizedType<HibuFacet>): JSX.Element[] => {

        const allMatchingFacetValues = facets.allKeys.filter((k) => {
            const facet: HibuFacet = facets.byKey[k] as HibuFacet;
            return (
                // if not filtered will return all facet values
                !filter ||
                // if facet value's name includes the filter term
                facet.name.toLowerCase().includes(filter.toLowerCase()) ||
                // if facet value has been selected then include
                (hasSelectedFacetValues() && props.selectedFacetValues.includes(k))
            );
        });

        const facetValuesToBeDisplayedWhenShowMoreIsFalse = getFacetValuesToBeDisplayed(allMatchingFacetValues);
        const facetValuesToBeDisplayed: string[] = showMore ? allMatchingFacetValues : facetValuesToBeDisplayedWhenShowMoreIsFalse;
        unselectedFacetValuesThatAreVisibleWhenShowMoreIsFalseCount = facetValuesToBeDisplayedWhenShowMoreIsFalse.length - getSelectedFacetValuesCount();
        allMatchingFacetValuesCount = allMatchingFacetValues.length;

        return facetValuesToBeDisplayed.map((k) => {
            const facet: HibuFacet = facets.byKey[k] as HibuFacet;
            const facetComponent = (
                <FacetComponent
                    focus={props.withTooltip && tooltipValue === facet.value}
                    key={facet.value}
                    facet={facet}
                    multiSelect={isMultiSelect}
                    onFacetToggled={() => handleFacetToggled(facet)}
                    onFacetMouseEnter={() => handleFacetMouseEnter(facet)}
                    onFacetMouseLeave={() => handleFacetMouseLeave(facet)}
                    selected={isFacetSelected(facet)}
                    showCounts={props.group.showCounts}
                />
            );
            return props.withTooltip && tooltipValue === facet.value ? (
                <HibuTooltip
                    key={`facet_tooltip_${facet.value}`}
                    tooltipKey={facet.value}
                    translate={false}
                    defaultShow={true}
                >
                    {facetComponent}
                </HibuTooltip>
            ) : (
                facetComponent
            );
        });
    };

    const renderExpandedHistogram = (facets: ReduxNormalizedType<HibuFacet>) => {
        const facetComponents: JSX.Element[] = [];
        facetComponents.push(
            <FacetHistogramComponent
                key={props.group.fieldName}
                facetGroupFieldName={props.group.fieldName}
                facets={facets}
                onFacetRangeUpdated={handleFacetRangeUpdated}
                selectedFacetValues={props.selectedFacetValues}
            />
        );
        return facetComponents;
    };

    const renderCollapsedHistogram = () => {
        const collapsedHistogramComponent: JSX.Element[] = [];
        const values: string[] = props.selectedFacetValues;
        let begin: number, end: number;
        if (values?.length === 2) {
            begin = new Date(values[0]).valueOf();
            end = new Date(values[1]).valueOf();
            collapsedHistogramComponent.push(
                <div
                    className="facet facet-selected"
                    onClick={() => props.onChange(props.group, [])}
                >
                    <div className="facet-value" key={begin}>
                        <span
                            className="facet-value-text">{formatDateShort(begin) + ' - ' + formatDateShort(end)}</span>
                        <SvgIcon icon={Icons.X} className="facet-deselect-icon"/>
                    </div>
                </div>
            );
        }
        return collapsedHistogramComponent;
    };

    const renderShowMore = () => {
        // determine the delta of values displayed with showMore vs. !showMore -- all three values are the same irrespective of the current value of showMore
        const facetValuesNotShownWhenShowMoreIsFalse =
            allMatchingFacetValuesCount - unselectedFacetValuesThatAreVisibleWhenShowMoreIsFalseCount - getSelectedFacetValuesCount();
        if (
            (props.group.collapsible && !props.isExpanded) || // facet group is collapsed
            (facetValuesNotShownWhenShowMoreIsFalse <= 0) // we would display the same facet values for 'show more' as for 'show less'
        ) {
            // Do not display the 'show more' component
            return null;
        }
        const formattedValues = formatNumber(facetValuesNotShownWhenShowMoreIsFalse)
        const showMoreLabel = showMore
            ? t('general.hideAll')
            : t('general.seeAll', {entry: formattedValues});
        // the label "seeAll" assumes that the backend did in fact send all facet values -- make sure to configure the backend/Elasticsearch accordingly
        return (
            <span className='show-more-facet-values' onClick={() => setShowMore(!showMore)}>
                {showMoreLabel}
            </span>
        );
    };

    const getFacetValuesToBeDisplayed = (allMatchingFacetValues): string[] => {
        //filter the top DEFAULT_FACET_VALUES_VISIBLE_COUNT facet values plus all selected ones among the remaining facet values
        return allMatchingFacetValues.filter((k, i) => {
            return (i < DEFAULT_FACET_VALUES_VISIBLE_COUNT || (hasSelectedFacetValues() && props.selectedFacetValues.includes(k)));
        });
    };

    const getSelectedFacetValuesCount = (): number => {
        return props.selectedFacetValues ? props.selectedFacetValues.length : 0;
    };

    const isCollapsed = (): boolean => {
        return hasFacetValues() && (props.group.collapsible && !props.isExpanded);
    };

    const isFacetSelected = (facet: HibuFacet): boolean => {
        return !!props.selectedFacetValues && !!props.selectedFacetValues.find((value) => value === facet.value);
    };

    const hasFacetValues = (): boolean => {
        return props.group.hibuFacetValues && props.group.hibuFacetValues.length > 0;
    };

    const hasSelectedFacetValues = (): boolean => {
        return props.selectedFacetValues && props.selectedFacetValues.length > 0;
    };

    // Returns selected facet values, whilst retaining order of the original normalized values
    const toSelectedHibuFacetValues = (): ReduxNormalizedType<HibuFacet> => {
        const selectedFacetValues: ReduxNormalizedType<HibuFacet> = {allKeys: [], byKey: {}};
        if (props.selectedFacetValues !== undefined) {
            props.normalizedValues.allKeys
                .filter((facetValueId) => props.selectedFacetValues && props.selectedFacetValues.includes(facetValueId))
                .forEach((facetValueId) => {
                    selectedFacetValues.allKeys.push(facetValueId);
                    selectedFacetValues.byKey[facetValueId] = props.normalizedValues.byKey[facetValueId];
                });

        }
        return selectedFacetValues;
    };

    return (
        <ListGroupItem key={props.group.fieldName} className={props.isExpanded ? 'expanded' : 'collapsed'}>
            <FacetGroupHeader
                onClick={onClickHeader}
                filter={filter}
                setFilter={setFilter}
                isExpanded={props.isExpanded}
                fieldName={props.group.fieldName}
                isSearchable={props.selectionType !== SelectionType.RANGE}
            />
            {renderFacetContainer()}
        </ListGroupItem>
    );
});
